import { HttpClient } from '@packages/core/http';
import { ITechlineResponse, ITechline, ITechlineRedux } from './techline.state';
import {
    Account,
    CaseTypesResponse,
    RetrieveCaseResponse,
    SaveCaseTypeRequestBody,
    WorksheetCaseTypeStatus,
    CreateCidCvnDraftCaseResponse,
    CreateCidCvnDraftCaseParams,
} from '@packages/models/api';

export interface CaseTechnicianAssignment {
    caseTechnicianAssignmentId: string;
    caseNumber: string;
    name?: string;
    technicianName?: string;
    assignmentType?: string;
    description?: string;
    completed: boolean;
    assignedDate?: string;
    completedDate?: string;
    priority?: string;
    estimatedHours?: number;
    actualHours?: number;
    status?: number;
}

export interface CaseTechnicianAssignmentsResponse {
    results: CaseTechnicianAssignment[];
    statusCode: number;
    status: string;
    message: string;
    totalRecords: string;
}

type PublishedWorksheetsResponse = string[];

type CreateCidCvnDraftCaseRequestBody = {
    retailernumber: string;
    username: string;
    vin: string;
    cidcvncalibrationid: string;
    cidcvncalibrationverificationnumber: string;
    cidcvnstatus: string;
    checkforcpo: boolean;
};

const toCreateCidCvnDraftCaseRequestBody = (params: CreateCidCvnDraftCaseParams): CreateCidCvnDraftCaseRequestBody => ({
    retailernumber: params.retailerNumber,
    username: params.userName,
    vin: params.vin,
    cidcvncalibrationid: params.cid,
    cidcvncalibrationverificationnumber: params.cvn,
    cidcvnstatus: params.cidCvnStatusId,
    checkforcpo: params.cpoInd,
});

export class TechlineService {
    constructor(private http: HttpClient) {}

    public getDashboardCases = async ({
        retailerNumber,
        myCases,
        page,
        recordCount,
        trigger,
        ignoreCache = true,
        signal,
    }: {
        retailerNumber: string;
        myCases?: boolean;
        page?: number;
        recordCount?: number;
        trigger?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        const queryString = this.http.queryString({
            retailerNumber,
            myCases,
            page,
            recordCount,
            trigger,
        });

        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/techline/dashboard/cases${queryString}`,
            ignoreCache,
            signal,
        });
    };

    public getDashboardMetrics = async ({
        retailerNumber,
        myCases,
        ignoreCache = true,
        signal,
    }: {
        retailerNumber: string;
        myCases?: boolean;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        const queryString = this.http.queryString({
            retailerNumber,
            myCases,
        });

        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/techline/dashboard/metrics${queryString}`,
            ignoreCache,
            signal,
        });
    };

    public getTechline = async ({
        techlineId,
        ignoreCache = false,
        signal,
    }: {
        techlineId?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<ITechlineRedux>({
            method: 'GET',
            url: '/techline' + this.http.queryString({ techlineId }),
            ignoreCache,
            signal,
        });
    };

    public createTechline = async ({
        ignoreCache = false,
        techline,
        signal,
    }: {
        ignoreCache?: boolean;
        techline: Partial<ITechline>;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<ITechlineResponse>({
            method: 'POST',
            url: '/techline',
            ignoreCache,
            body: {
                techline,
            },
            signal,
        });
    };

    public updateTechline = async ({
        techlineId,
        techline,
        ignoreCache = false,
        signal,
    }: {
        techlineId: string;
        techline: Partial<ITechline>;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<ITechlineRedux>({
            method: 'PUT',
            url: `/techline/${techlineId}`,
            body: {
                techline,
            },
            ignoreCache,
            signal,
        });
    };

    public deleteTechline = async ({
        techlineId,
        ignoreCache = false,
        signal,
    }: {
        techlineId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<void>({
            method: 'DELETE',
            url: `/techline/${techlineId}`,
            ignoreCache,
            signal,
        });
    };

    public getInfotainmentPortalKey = async ({
        retailerId,
        ignoreCache = false,
        signal,
    }: {
        retailerId?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<{
            encryptedRetailerId: string;
            infotainmentUrl: string;
        }>({
            method: 'GET',
            url: `/infotainment/retailer/${retailerId}`,
            ignoreCache,
            signal,
        });
    };

    public getInfotainmentCasesPortalKey = async ({
        retailerId,
        ignoreCache = false,
        signal,
    }: {
        retailerId?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<{
            encryptedRetailerId: string;
            infotainmentUrl: string;
        }>({
            method: 'GET',
            url: `/infotainment/retailercases/${retailerId}`,
            ignoreCache,
            signal,
        });
    };

    public validateVin = async (vinNumber: string, ignoreCache = true, signal?: AbortSignal) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/validateVin/v1/${this.http.queryString({ vin: vinNumber })}`,
            ignoreCache,
            signal,
        });
    };

    public retrieveVinDetails = async (vinNumber: string, ignoreCache = true, signal?: AbortSignal) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveVinDetails/v1/${this.http.queryString({ vin: vinNumber })}`,
            ignoreCache,
            signal,
        });
    };

    public retrieveRetailer = async (ignoreCache = true, signal?: AbortSignal, query?: string) => {
        const queryString = this.http.queryString({ query });
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveRetailer/v1${queryString}`,
            ignoreCache,
            signal,
        });
    };

    public recentSearch = async (ignoreCache = true, signal?: AbortSignal) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/retailers/recent-search',
            ignoreCache,
            signal,
        });
    };

    patchRetailerProfile = (data: {
        casenumber: string;
        username: string;
        retailernumber: string;
        statuscode: number;
    }) => {
        return this.http.orchestrateRequest({
            method: 'PATCH',
            url: '/northstar/patchCaseRetailerProfile/v1',
            ignoreCache: true,
            body: data,
        });
    };

    public retrieveCases = async (
        retailerNumber: string,
        ignoreCache = true,
        signal?: AbortSignal,
        techlineCaseStatus?: string,
        isMobile?: boolean,
        page?: number,
        recordCount?: number,
        vinFilter?: string,
        caseTypeFilter?: string,
        submissionDateFilter?: string
    ) => {
        if (!retailerNumber) {
            return;
        }
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieve-cases${this.http.queryString({
                retailerNumber,
                page,
                recordCount,
                techlineCaseStatus,
                isMobile,
                vinFilter,
                caseTypeFilter,
                submissionDateFilter,
            })}`,
            ignoreCache,
            signal,
        });
    };

    public searchCases = async (
        retailerNumber: string,
        searchText: string,
        ignoreCache = true,
        signal?: AbortSignal
    ) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/searchText${this.http.queryString({
                searchText,
                retailerNumber,
            })}`,
            ignoreCache,
            signal,
        });
    };

    public retrieveTechlinecases = async (
        techlineFilters: {
            retailerNumber: string;
            page?: number;
            recordCount?: number;
            techlineCaseStatus?: string;
            caseNumber?: string;
            vinNumber?: string;
            submissionDate?: string;
            worksheets?: string;
        },
        ignoreCache = true,
        signal?: AbortSignal
    ) => {
        const queryParams: any = {
            retailerNumber: techlineFilters.retailerNumber,
            page: techlineFilters.page || 1,
            recordCount: techlineFilters.recordCount || 10,
        };

        if (techlineFilters.techlineCaseStatus && techlineFilters.techlineCaseStatus.trim() !== '') {
            queryParams.techlineCaseStatus = techlineFilters.techlineCaseStatus;
        }
        if (techlineFilters.caseNumber) {
            queryParams.caseNumber = techlineFilters.caseNumber;
        }
        if (techlineFilters.vinNumber) {
            queryParams.vin = techlineFilters.vinNumber;
        }
        if (techlineFilters.submissionDate) {
            queryParams.submissionDate = techlineFilters.submissionDate;
        }
        if (techlineFilters.worksheets) {
            queryParams.worksheets = techlineFilters.worksheets;
        }

        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieve-cases${this.http.queryString(queryParams)}`,
            ignoreCache,
            signal,
        });
    };

    public retrieveCaseTypes = async (ignoreCache = true, signal?: AbortSignal) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/northstar/retrieveCaseTypes/v1',
            ignoreCache,
            signal,
        });
    };

    public createDraftCase = async ({
        username,
        retailernumber,
        vin,
        signal,
        ignoreCache = true,
    }: {
        username: string;
        retailernumber: string;
        vin: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'PATCH',
            url: '/northstar/draftCase/v1',
            body: {
                username,
                retailernumber,
                vin,
            },
            ignoreCache,
            signal,
        });
    };

    public createCase = async ({
        casenumber,
        dtccode,
        signal,
        ignoreCache = true,
    }: {
        casenumber: string;
        dtccode: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'PATCH',
            url: '/northstar/createCase',
            body: {
                casenumber,
                dtccode,
            },
            ignoreCache,
            signal,
        });
    };

    public updateCase = async ({
        updateBody,
        signal,
        ignoreCache = true,
    }: {
        updateBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'PATCH',
            url: '/northstar/draftCase/v1',
            body: updateBody,
            ignoreCache,
            signal,
        });
    };

    public getCaseDetails = async ({
        caseNumber,
        signal,
        ignoreCache = true,
    }: {
        caseNumber: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<RetrieveCaseResponse>({
            method: 'GET',
            url: `/northstar/retrieveCase/${caseNumber}/v1`,
            ignoreCache,
            signal,
        });
    };

    public getDtcCodes = async ({
        query,
        signal,
        ignoreCache = true,
    }: {
        query: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        const queryString = this.http.queryString({ query });
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/dtc-codes' + queryString,
            ignoreCache,
            signal,
        });
    };

    public updateCaseStatus = async ({
        updateBody,
        signal,
        ignoreCache = true,
    }: {
        updateBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/northstar/updateCaseStatus/v1',
            body: updateBody,
            ignoreCache,
            signal,
        });
    };

    public deleteCase = async ({
        caseNumber,
        ignoreCache = false,
        signal,
    }: {
        caseNumber: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/northstar/deleteDraftCase/${caseNumber}/v1`,
            ignoreCache,
            signal,
        });
    };

    public addComment = async ({
        body,
        signal,
        ignoreCache = true,
    }: {
        body: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/northstar/createTechShareComment/v1',
            body: body,
            ignoreCache,
            signal,
        });
    };

    public getComments = async ({
        caseNumber,
        signal,
        ignoreCache = true,
    }: {
        caseNumber: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveTechShareComments/${caseNumber}/v1`,
            ignoreCache,
            signal,
        });
    };

    public updateDTCCode = async ({
        updateBody,
        signal,
        ignoreCache = true,
    }: {
        updateBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'PATCH',
            url: '/northstar/patchDTCCode/v1',
            body: updateBody,
            ignoreCache,
            signal,
        });
    };

    public postAssets = async ({
        assetsBody,
        signal,
        ignoreCache = true,
    }: {
        assetsBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/northstar/postAssets/v1',
            body: assetsBody,
            ignoreCache,
            signal,
        });
    };

    public uploadAssets = async ({
        uploadUrl,
        headers,
        assetsBody,
        onProgress,
    }: {
        uploadUrl: string;
        headers: any;
        assetsBody: any;
        onProgress?: any;
    }) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        Object.entries(headers).forEach(([header, value]: any) => {
            xhr.setRequestHeader(header, value);
        });
        xhr.upload.addEventListener('progress', (evt) => {
            if (evt.lengthComputable) {
                const progress = Math.round((evt.loaded * 100) / evt.total);
                onProgress?.(progress);
            }
        });
        xhr.send(assetsBody);
    };

    public updateAssets = async ({
        assetsBody,
        signal,
        ignoreCache = true,
    }: {
        assetsBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/northstar/updateAssets',
            body: assetsBody,
            ignoreCache,
            signal,
        });
    };

    searchFailCodes({
        modelYear,
        modelCode,
        optionCode,
        query,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        query?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/techline/fail-codes/search' + this.http.queryString({ modelYear, modelCode, optionCode, query });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    fetchPossibleSections({
        modelYear,
        modelCode,
        optionCode,
        failCode,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        failCode?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const queryString = this.http.queryString({ modelYear, modelCode, optionCode, failCode });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/techline/fail-codes/fail-code-sections' + queryString,
            ignoreCache,
            signal,
        });
    }

    fetchPossibleSubsections({
        modelYear,
        modelCode,
        optionCode,
        failCode,
        sectionNumber,
        sectionName,
        ignoreCache,
        signal,
    }: {
        failCode?: string;
        sectionNumber: number;
        sectionName: string;
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const queryString = this.http.queryString({
            modelYear,
            modelCode,
            optionCode,
            failCode,
            sectionNumber,
            sectionName,
        });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/techline/fail-codes/fail-code-subsections' + queryString,
            ignoreCache,
            signal,
        });
    }

    fetchFailCodeSections({
        modelYear,
        modelCode,
        optionCode,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url = '/techline/fail-codes/sections' + this.http.queryString({ modelYear, modelCode, optionCode });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    lookupFailCodeSubsections({
        modelYear,
        modelCode,
        optionCode,
        sectionNumber,
        sectionName,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        sectionNumber: number;
        sectionName: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url =
            '/techline/fail-codes/subsections' +
            this.http.queryString({ modelYear, modelCode, optionCode, sectionNumber, sectionName });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    lookupFailCodeFigures({
        modelYear,
        modelCode,
        optionCode,
        sectionNumber,
        sectionName,
        subsectionNumber,
        subsectionName,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        sectionNumber: number;
        sectionName: string;
        subsectionNumber: number;
        subsectionName: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url =
            '/techline/fail-codes/figures' +
            this.http.queryString({
                modelYear,
                modelCode,
                optionCode,
                sectionNumber,
                sectionName,
                subsectionNumber,
                subsectionName,
            });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    lookupFailCodes({
        modelYear,
        modelCode,
        optionCode,
        sectionNumber,
        subsectionNumber,
        figureCode,
        partNumber,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        sectionNumber?: number;
        subsectionNumber?: number;
        figureCode?: string;
        partNumber?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const queryString = partNumber
            ? this.http.queryString({ modelYear, modelCode, optionCode, partNumber })
            : this.http.queryString({ modelYear, modelCode, optionCode, sectionNumber, subsectionNumber, figureCode });

        const url = '/techline/fail-codes' + queryString;

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    searchPartNumbers({
        modelYear,
        modelCode,
        optionCode,
        query,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        query?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const url =
            '/techline/fail-codes/part-number/search' +
            this.http.queryString({ modelYear, modelCode, optionCode, query });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url,
            ignoreCache,
            signal,
        });
    }

    fetchFailCodeWiringDiagramSubsections({
        modelYear,
        modelCode,
        optionCode,
        vin,
        ignoreCache,
        signal,
    }: {
        modelCode?: string;
        modelYear?: string;
        optionCode?: string;
        vin?: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const queryString = this.http.queryString({ modelYear, modelCode, optionCode, vin });

        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: '/techline/fail-codes/electrical-wiring-diagrams/subsection-figures' + queryString,
            ignoreCache,
            signal,
        });
    }

    /**
     * Fetch a wiring diagram image as a blob for a given figure code.
     *
     * @param figureCode - the figure code identifying the diagram to fetch
     * @param signal - optional abort signal for cancellation
     *
     * @returns the diagram image as a Blob
     */
    fetchWiringDiagramImage({ figureCode, signal }: { figureCode: string; signal?: AbortSignal }) {
        const queryString = this.http.queryString({ figureId: figureCode });

        return this.http.orchestrateRequest<Blob>({
            method: 'GET',
            url: '/fail-codes/electrical-wiring-diagram' + queryString,
            responseType: 'blob',
            signal,
        });
    }

    public retrieveCaseAssets = async ({
        caseNumber,
        signal,
        ignoreCache = true,
    }: {
        caseNumber: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveCaseAssets/${caseNumber}/v1`,
            ignoreCache,
            signal,
        });
    };

    retrieveAsset(assetId: string) {
        return this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveAsset/${assetId}/v1`,
            ignoreCache: true,
        });
    }

    public retrieveAssetUploadStatus = async ({
        assetId,
        signal,
        ignoreCache = true,
    }: {
        assetId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) => {
        return await this.http.orchestrateRequest<any>({
            method: 'GET',
            url: `/northstar/retrieveAssetUploadStatus/${assetId}/v1`,
            ignoreCache,
            signal,
        });
    };

    fetchCaseType({ status, isPilotUser }: { status: WorksheetCaseTypeStatus; isPilotUser?: boolean }) {
        return this.http.orchestrateRequest<CaseTypesResponse>({
            method: 'GET',
            url: `/admin/casetypes?status=${status}${isPilotUser ? '&pilotUser=true' : '&pilotUser=false'}`,
        });
    }

    fetchPublishedWorksheets() {
        return this.http.orchestrateRequest<PublishedWorksheetsResponse>({
            method: 'GET',
            url: `/techline/published-worksheets`,
        });
    }

    saveCaseType(body: SaveCaseTypeRequestBody) {
        return this.http.orchestrateRequest<any>({
            method: 'PATCH',
            url: '/northstar/caseTypeIntersect/v1',
            body,
        });
    }

    fetchRetailerCaseMembers({
        retailerId,
        ignoreCache,
        signal,
    }: {
        retailerId: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<{ accounts: Account[] }>({
            method: 'GET',
            url: `/retailers/${retailerId}/techline-case-members`,
            ignoreCache,
            signal,
        });
    }

    updateRetailerCaseMembers({
        retailerId,
        accountIds,
        ignoreCache,
        signal,
    }: {
        retailerId: string;
        accountIds: string[];
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest({
            method: 'PUT',
            url: `/retailers/${retailerId}/techline-case-members`,
            body: { accountIds },
            ignoreCache,
            signal,
        });
    }

    createCidCvnTechlineDraftCase({
        body: params,
        signal,
        ignoreCache = true,
    }: {
        body: CreateCidCvnDraftCaseParams;
        signal?: AbortSignal;
        ignoreCache?: boolean;
    }) {
        const body = toCreateCidCvnDraftCaseRequestBody(params);

        return this.http.orchestrateRequest<CreateCidCvnDraftCaseResponse>({
            method: 'PATCH',
            url: `/northstar/cid-cvn/draftCase/v1`,
            body,
            signal,
            ignoreCache,
        });
    }

    getWorksheetTitles({
        isPilotUser,
        ignoreCache = true,
        signal,
    }: {
        isPilotUser: boolean;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any[]>({
            method: 'GET',
            url: `/techline/worksheet-titles?isPilotUser=${isPilotUser}`,
            ignoreCache,
            signal,
        });
    }

    deleteCidCvnCase({ caseNumber, ignoreCache = false }: { caseNumber: string; ignoreCache?: boolean }) {
        return this.http.orchestrateRequest<any>({
            method: 'DELETE',
            url: `/northstar/deleteCidCvnDraftCase/${caseNumber}/v1`,
            ignoreCache,
        });
    }

    updateCidCvnCaseStatus({
        updateBody,
        signal,
        ignoreCache = true,
    }: {
        updateBody: any;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: '/northstar/cid-cvn/updateCaseStatus/v1',
            body: updateBody,
            ignoreCache,
            signal,
        });
    }

    updateAssignmentCompletionStatus({
        casetechnicianassignmentid,
        technicianname,
        completed,
        signal,
        ignoreCache = true,
    }: {
        casetechnicianassignmentid: string;
        technicianname: string;
        completed: boolean;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        const body = {
            casetechnicianassignmentid,
            technicianname,
            completed,
        } as any;

        return this.http.orchestrateRequest<any>({
            method: 'POST',
            url: `/northstar/case-technician-assignment`,
            body,
            signal,
            ignoreCache,
        });
    }

    getCaseTechnicianAssignments({
        caseNumber,
        ignoreCache = true,
        signal,
    }: {
        caseNumber: string;
        ignoreCache?: boolean;
        signal?: AbortSignal;
    }) {
        return this.http.orchestrateRequest<CaseTechnicianAssignmentsResponse>({
            method: 'GET',
            url: `/northstar/case-technician-assignment/${caseNumber}`,
            ignoreCache,
            signal,
        });
    }
}
